import React from "react";
import {
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from "react-icons/lu";

import Button from "../ui/Button";
import PageSizeSelect from "../ui/PageSizeSelect";

type Props = {
  itemsPerPage: number;
  totalItems: number;
  paginate: (pageNumber: number) => void;
  currentPage: number;
  onPageSizeChange?: (size: number) => void;
  itemLabel?: string;
};

const Pagination = ({
  itemsPerPage,
  totalItems,
  paginate,
  currentPage,
  onPageSizeChange,
  itemLabel,
}: Props) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const renderPageButtons = () => {
    const startPage = Math.max(1, Math.min(totalPages - 4, currentPage - 2));
    const endPage = startPage + Math.min(5, totalPages) - 1;
    const buttons: React.ReactElement[] = [];

    if (startPage > 1) {
      buttons.push(
        <Button
          key={1}
          variant="outline"
          size="iconSm"
          onClick={() => paginate(1)}
        >
          1
        </Button>
      );

      if (startPage > 2) {
        buttons.push(
          <span key="start-ellipsis" className="px-1 text-gray-400">
            ...
          </span>
        );
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="iconSm"
          onClick={() => paginate(page)}
        >
          {page}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <span key="end-ellipsis" className="px-1 text-gray-400">
            ...
          </span>
        );
      }

      buttons.push(
        <Button
          key={totalPages}
          variant="outline"
          size="iconSm"
          onClick={() => paginate(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-500">
          {(currentPage - 1) * itemsPerPage + 1}-
          {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
          {itemLabel ? ` ${itemLabel}` : ""}
        </div>

        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm">પ્રતિ પેજ:</span>
            <PageSizeSelect value={itemsPerPage} onChange={onPageSizeChange} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="iconSm"
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        >
          <LuChevronsLeft className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="iconSm"
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          <LuChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-1 max-sm:hidden">
          {renderPageButtons()}
        </div>

        <span className="px-2 text-sm font-medium sm:hidden">
          {currentPage} / {totalPages}
        </span>

        <Button
          variant="outline"
          size="iconSm"
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          <LuChevronRight className="h-4 w-4" />
        </Button>

        <Button
          variant="outline"
          size="iconSm"
          onClick={() => paginate(totalPages)}
          disabled={currentPage === totalPages}
        >
          <LuChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
